import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Identifiable,
  Project,
  ProjectConfiguration,
  ProjectPortfolio,
  zone as z,
} from "@planzone/dto";
import {
  useRetrieveProjectToolsConfiguration,
  useDuplicateProject,
} from "@planzone/view-models";
import { Modal } from "@common/modal";
import { Button } from "@common/button";
import { ProjectCreationForm } from "../../projectCreationForm";
import { Props } from "./types";
import styles from "./styles.scss";

function ProjectEdition({
  zone,
  sourceProject,
  isMultiProject,
  selectZone,
  close,
}: Props) {
  const { t } = useTranslation();
  const { isRequestPending, duplicateProject } = useDuplicateProject(
    zone || { ...z, uuid: "" },
    sourceProject,
  );
  const {
    isRequestPending: isConfRequestPending,
    isRequestSuccess: isConfRequestSuccess,
    isRequestFailure: isConfRequestFailure,
    config,
    retrieveProjectToolsConfiguration,
  } = useRetrieveProjectToolsConfiguration(sourceProject);
  const [formData, setFormData] = React.useState<
    Project & { portfolios: Identifiable<ProjectPortfolio>[] }
  >({
    ...sourceProject,
    isTemplate: false,
    portfolios: [],
  });
  const projectDuration = sourceProject.rootList.endDate
    ? new Date(sourceProject.rootList.endDate).getTime() -
      new Date(sourceProject.rootList.startDate).getTime()
    : 0;

  const [configuration, setConfiguration] =
    useState<ProjectConfiguration>(config);

  React.useEffect(() => {
    !isConfRequestPending &&
      !isConfRequestSuccess &&
      !isConfRequestFailure &&
      retrieveProjectToolsConfiguration(sourceProject);
  }, []);

  React.useEffect(() => {
    setConfiguration(config);
  }, [config]);

  React.useEffect(() => {
    formData.rootList.startDate &&
      formData.rootList.endDate &&
      setFormData({
        ...formData,
        rootList: {
          ...formData.rootList,
          endDate: new Date(
            new Date(formData.rootList.startDate).getTime() + projectDuration,
          ).toISOString(),
        },
      });
  }, [formData.rootList.startDate]);

  return (
    <Modal header={t("projectDuplication.title")} close={close}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          zone &&
            duplicateProject({
              zone,
              sourceProject,
              project: formData,
              configuration,
            });
        }}
        className={styles.form}
      >
        <div className={styles.main}>
          <ProjectCreationForm
            zone={zone}
            formData={formData}
            configuration={configuration}
            editForm={setFormData}
            setConfiguration={setConfiguration}
            isMultiProject={isMultiProject}
            template={sourceProject}
            selectZone={selectZone}
          />
        </div>
        <div className={styles.buttons}>
          <Button type="cancel" press={close}>
            {t("projectDuplication.cancel")}
          </Button>
          <Button
            type="submit"
            status={isRequestPending ? "pending" : "none"}
            isDisabled={!zone}
          >
            {t("projectDuplication.submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export { ProjectEdition };
